import { FUNERAL_SIDO, FUNERAL_SIGUNGU, cleanSigunguWord } from "goi_common";
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import parlors_discount from "@/constants/parlors_discount_detail.json";
import { ParlorDetailProps } from "@/types/parlor";
import { getParlorInfoByRegionCode } from "@/utils/getRegionParlorInfo";
import { getRegionParlorStats, RegionParlorStats } from "@/utils/getRegionParlorStats";
import { ParsedUrlQuery } from "querystring";
import ParlorsRegionCodeSigunguContainer from "@/containers/Parlor/ParlorRegionContainer/ParlorsRegionCodeSigunguContainer";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";

interface Path {
  params: ParsedUrlQuery;
  locale?: string | undefined;
}

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult = skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) {
    return skipBuildResult;
  }

  const sidoList = Array.from(FUNERAL_SIDO.keys());
  const paths: Path[] = [];
  sidoList.forEach((sido) => {
    const sigunguKeys = FUNERAL_SIGUNGU.get(sido)?.keys() as IterableIterator<number>;

    [...sigunguKeys].forEach((sigungu) => {
      paths.push({
        params: {
          code: String(sido),
          sigungu: String(sigungu),
        },
      });
    });
  });

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { code: sidoCode, sigungu: sigunguCode } = params as { code: string; sigungu: string };
  const numberedSidoCode = Number(sidoCode);
  const numberedSigunguCode = Number(sigunguCode);

  const sidoName = FUNERAL_SIDO.get(numberedSidoCode);
  const sigunguName = FUNERAL_SIGUNGU.get(numberedSidoCode)?.get(numberedSigunguCode) as string;
  const cleanedSigunguName = cleanSigunguWord(sigunguName);

  const { regionParlors, hasSigunguParlor } = getParlorInfoByRegionCode(
    numberedSidoCode,
    numberedSigunguCode,
  );

  const sortedRegionParlors = regionParlors.sort((p) =>
    parlors_discount.map((dp) => dp.parlor_id).includes(Number(p.id)) ? -1 : 1,
  );

  const regionStats = getRegionParlorStats(sortedRegionParlors as unknown as ParlorDetailProps[]);

  const representativeNames = sortedRegionParlors
    .slice(0, 2)
    .map((p) => p.companyname)
    .filter(Boolean);
  const representativeText =
    representativeNames.length > 0 ? `${representativeNames.join(", ")} 등 ` : "";

  // 시군구에 시설이 없으면 regionStats는 시도 fallback 값이라 "N곳"을 시군구에 붙이면 거짓이 된다.
  // 이 경우 카운트를 아예 빼고 지역명만 남긴다 ("0곳"으로 찍히거나 시도 숫자를 시군구에 붙이는 것보다 나음)
  const countSuffix = hasSigunguParlor ? ` ${regionStats.parlorCount}곳` : "";

  const description = `${sidoName} ${cleanedSigunguName} 지역 장례식장${countSuffix}의 시설정보, 빈소 비용, 위치를 비교하세요. ${representativeText}공설·사설 구분과 주차, 편의시설 정보까지 한눈에 확인할 수 있습니다.`;

  const title = `${cleanedSigunguName} 장례식장${countSuffix} | ${sidoName} 장례식장 정보`;

  return {
    props: {
      regionParlors,
      regionStats,
      hasSigunguParlor,
      sidoName,
      sido: sidoCode,
      sigunguName: cleanedSigunguName,
      sigungu: sigunguCode,
      isSigungu: true,
      pageDescription: description,
      pageKeywords: `${cleanedSigunguName} 장례식장, ${cleanedSigunguName} 장례식장 비용, ${sidoName} ${cleanedSigunguName} 장례식장`,
      pageOgTitle: title,
      pageOgDescription: description,
      pageOgImage:
        regionParlors[0]?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      // 숫자 라우트(/parlors/region/11/11240/)는 name 라우트(/parlors/region/서울-송파구/)와 완전 중복.
      // 내부링크·sitemap이 쓰는 name 라우트를 대표 URL로 지정 (cleanSigunguWord 적용 전 원본 sigunguName 사용)
      pageCanonical: `https://www.goifuneral.co.kr/parlors/region/${encodeURIComponent(
        `${sidoName}-${sigunguName}`,
      )}/`,
    },
  };
};

interface ParlorRegionPageProps {
  regionParlors: Array<ParlorDetailProps>;
  regionStats: RegionParlorStats;
  hasSigunguParlor: boolean;
  sido: string;
  sidoName: string;
  sigungu?: string;
  sigunguName?: string;
  isSigungu: boolean;
}

function ParlorRegionSidoSigunguPage(props: ParlorRegionPageProps) {
  const {
    sido,
    sidoName,
    sigungu,
    sigunguName,
    isSigungu,
    regionParlors,
    regionStats,
    hasSigunguParlor,
  } = props;

  return (
    <ParlorsRegionCodeSigunguContainer
      regionParlors={regionParlors}
      regionStats={regionStats}
      hasSigunguParlor={hasSigunguParlor}
      sido={sido}
      sidoName={sidoName}
      sigungu={sigungu}
      sigunguName={sigunguName}
      isSigungu={isSigungu}
    />
  );
}

export default ParlorRegionSidoSigunguPage;
